// Login real do console (#4340): código de 6 dígitos no e-mail, pelo fluxo USER_AUTH do Cognito.
// Chama a API pública do Cognito direto do navegador (sem SDK, para o bundle continuar pequeno) e
// sem Hosted UI: a tela de login é a do próprio console.
//
//   e-mail  → InitiateAuth(USER_AUTH, PREFERRED_CHALLENGE=EMAIL_OTP) → Session + e-mail com o código
//   código  → RespondToAuthChallenge(EMAIL_OTP)                       → access, ID e refresh token
//   renovar → InitiateAuth(REFRESH_TOKEN_AUTH), em voo único, 60 s antes de o access vencer
//   sair    → RevokeToken(refresh)
//
// O que corrige em relação ao bus-observer (observer-ui/src/auth.ts): guarda e usa o refresh token
// (a sessão não cai a cada hora), manda o ACCESS token para a API (não o ID token), renova sozinho
// e revoga no logout. Padrões de renovação portados de apps/canal/extensao_insights/src/auth/.

export interface CognitoConfig {
  region: string;
  clientId: string;
}

export interface Tokens {
  access: string;
  id: string;
  refresh: string;
  /** Vencimento do access token, em ms desde a época. */
  expiresAt: number;
}

/** Erro do Cognito com o `__type` dele (CodeMismatchException, NotAuthorizedException…). */
export class CognitoError extends Error {
  constructor(
    readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = "CognitoError";
  }
}

/** A sessão acabou de verdade (refresh recusado/revogado): só um novo login resolve. */
export class SessionEnded extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "SessionEnded";
  }
}

/** Renova quando faltar isto ou menos para o access token vencer. */
export const RENEW_MARGIN_MS = 60_000;

export interface RefreshStore {
  get(): string | null;
  set(value: string | null, remember: boolean): void;
  remembered(): boolean;
}

interface AuthResult {
  AccessToken: string;
  IdToken: string;
  RefreshToken?: string;
  ExpiresIn: number;
}

interface InitiateResponse {
  ChallengeName?: string;
  Session?: string;
  AuthenticationResult?: AuthResult;
}

export function createCognitoAuth(cfg: CognitoConfig, refreshStore: RefreshStore, deps: { fetch?: typeof fetch; now?: () => number } = {}) {
  const doFetch = deps.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => globalThis.fetch(input, init));
  const now = deps.now ?? (() => Date.now());
  const endpoint = `https://cognito-idp.${cfg.region}.amazonaws.com/`;

  let tokens: Tokens | null = null;
  let inFlight: Promise<Tokens> | null = null;
  let loggingOut = false;

  async function call<T>(target: string, body: Record<string, unknown>): Promise<T> {
    const r = await doFetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
      },
      body: JSON.stringify(body),
    });
    const j = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    if (!r.ok) {
      const type = String(j.__type ?? `HTTP${r.status}`).split("#").pop()!;
      throw new CognitoError(type, String(j.message ?? j.Message ?? r.statusText));
    }
    return j as T;
  }

  function keep(res: AuthResult, remember: boolean, currentRefresh?: string): Tokens {
    const refresh = res.RefreshToken ?? currentRefresh;
    if (!refresh) throw new Error("Cognito não devolveu refresh token");
    tokens = { access: res.AccessToken, id: res.IdToken, refresh, expiresAt: now() + res.ExpiresIn * 1000 };
    refreshStore.set(refresh, remember);
    return tokens;
  }

  /** Envia o código para o e-mail. Devolve a `Session` do desafio, que volta no `verify`. */
  async function start(email: string): Promise<string> {
    const r = await call<InitiateResponse>("InitiateAuth", {
      AuthFlow: "USER_AUTH",
      ClientId: cfg.clientId,
      AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "EMAIL_OTP" },
    });
    if (r.ChallengeName === "EMAIL_OTP" && r.Session) return r.Session;
    // Sem a preferência atendida, o Cognito devolve a lista de fatores; escolhemos o código no e-mail.
    if (r.ChallengeName === "SELECT_CHALLENGE" && r.Session) {
      const s = await call<InitiateResponse>("RespondToAuthChallenge", {
        ChallengeName: "SELECT_CHALLENGE",
        ClientId: cfg.clientId,
        Session: r.Session,
        ChallengeResponses: { USERNAME: email, ANSWER: "EMAIL_OTP" },
      });
      if (s.ChallengeName === "EMAIL_OTP" && s.Session) return s.Session;
    }
    throw new CognitoError("UnexpectedChallenge", `Desafio inesperado: ${r.ChallengeName ?? "nenhum"}`);
  }

  /** Confere o código. Em sucesso a sessão fica guardada; o refresh vai para o storage. */
  async function verify(email: string, session: string, code: string, remember: boolean): Promise<Tokens> {
    const r = await call<InitiateResponse>("RespondToAuthChallenge", {
      ChallengeName: "EMAIL_OTP",
      ClientId: cfg.clientId,
      Session: session,
      ChallengeResponses: { USERNAME: email, EMAIL_OTP_CODE: code },
    });
    if (!r.AuthenticationResult) throw new CognitoError("UnexpectedChallenge", `Desafio inesperado: ${r.ChallengeName ?? "nenhum"}`);
    return keep(r.AuthenticationResult, remember);
  }

  async function renewOnce(): Promise<Tokens> {
    const refresh = tokens?.refresh ?? refreshStore.get();
    if (!refresh) throw new SessionEnded("sem sessão guardada");
    try {
      const r = await call<InitiateResponse>("InitiateAuth", {
        AuthFlow: "REFRESH_TOKEN_AUTH",
        ClientId: cfg.clientId,
        AuthParameters: { REFRESH_TOKEN: refresh },
      });
      if (!r.AuthenticationResult) throw new Error("renovação sem tokens");
      if (loggingOut) throw new SessionEnded("logout em andamento");
      return keep(r.AuthenticationResult, refreshStore.remembered(), refresh);
    } catch (e) {
      // Só NotAuthorized (refresh vencido, revogado ou de outro client) encerra a sessão. Rede caída
      // ou erro do Cognito não desloga: a próxima chamada tenta de novo.
      if (e instanceof CognitoError && e.type === "NotAuthorizedException") {
        clear();
        throw new SessionEnded(e.message);
      }
      throw e;
    }
  }

  /** Voo único: requisições simultâneas compartilham a mesma renovação. */
  function renew(): Promise<Tokens> {
    if (loggingOut) return Promise.reject(new SessionEnded("logout em andamento"));
    inFlight ??= renewOnce().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  /** Access token válido para a próxima chamada, renovando antes de vencer. `null` = sem sessão. */
  async function accessToken(): Promise<string | null> {
    if (tokens && tokens.expiresAt - now() > RENEW_MARGIN_MS) return tokens.access;
    if (!tokens && !refreshStore.get()) return null;
    try {
      return (await renew()).access;
    } catch (e) {
      if (e instanceof SessionEnded) return null;
      throw e;
    }
  }

  function clear() {
    tokens = null;
    refreshStore.set(null, false);
  }

  /** Revoga o refresh no Cognito e limpa local. A limpeza local acontece mesmo se a revogação falhar. */
  async function logout(): Promise<boolean> {
    loggingOut = true;
    try {
      await inFlight?.catch(() => undefined);
      const refresh = tokens?.refresh ?? refreshStore.get();
      clear();
      if (!refresh) return true;
      try {
        await call("RevokeToken", { Token: refresh, ClientId: cfg.clientId });
        return true;
      } catch {
        return false;
      }
    } finally {
      loggingOut = false;
    }
  }

  return {
    start,
    verify,
    accessToken,
    /** Força uma renovação (depois de um 401). `null` = a sessão acabou. */
    forceRenew: async () => {
      if (tokens) tokens = { ...tokens, expiresAt: 0 };
      return accessToken();
    },
    hasSession: () => tokens !== null || refreshStore.get() !== null,
    idToken: () => tokens?.id ?? null,
    clear,
    logout,
  };
}

export type CognitoAuth = ReturnType<typeof createCognitoAuth>;

/** Mensagem para a pessoa a partir do erro do Cognito. */
export function loginErrorMessage(e: unknown): string {
  if (!(e instanceof CognitoError)) return "Não foi possível concluir o acesso. Verifique sua conexão e tente novamente.";
  switch (e.type) {
    case "CodeMismatchException":
      return "Código inválido. Confira os 6 dígitos e tente de novo.";
    case "ExpiredCodeException":
      return "O código expirou. Peça um novo.";
    case "NotAuthorizedException":
      return "Este código não vale mais. Peça um novo.";
    case "TooManyRequestsException":
    case "LimitExceededException":
      return "Muitas tentativas. Aguarde alguns minutos e tente de novo.";
    default:
      return "Não foi possível concluir o acesso. Tente novamente.";
  }
}
